import { useQuery } from "@tanstack/react-query";
import {
  Area,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { fetchTimeSeries, type YearStat } from "../lib/api";
import { num } from "../lib/format";

/**
 * Median €/m² over time, with the interquartile range behind it.
 *
 * The band matters as much as the line: a wide p25–p75 spread means the zone
 * mixes very different flats, so its median says less about any one of them.
 */
export function PriceChart({ scope, code }: { scope: string; code: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["timeseries", scope, code],
    queryFn: () => fetchTimeSeries(scope, code),
  });

  if (isLoading) {
    return <div className="h-32 animate-pulse rounded-lg bg-surface-alt" />;
  }
  if (isError || !data || data.years.length < 2) {
    return (
      <p className="text-[11px] text-ink-soft">
        Not enough years of data here to plot a trend.
      </p>
    );
  }

  // The band is a range area — a [low, high] tuple — rather than two stacked
  // areas. Stacking would anchor the axis at zero, which squashes a five-year
  // trend into a flat line.
  const rows = data.years.map((y: YearStat) => ({
    year: y.year,
    median: y.median_m2,
    range: [y.p25_m2, y.p75_m2] as [number, number],
    p25: y.p25_m2,
    p75: y.p75_m2,
    n: y.n,
  }));

  // An axis anchored at zero flattens the trend into a straight line — over a
  // five-year window the movement that matters is a few thousand €/m², not the
  // distance from zero. Pad the actual data range instead.
  const lo = Math.min(...rows.map((r) => r.p25));
  const hi = Math.max(...rows.map((r) => r.p75));
  const pad = Math.max((hi - lo) * 0.12, 250);

  return (
    <div className="h-36 w-full">
      <ResponsiveContainer>
        <ComposedChart data={rows} margin={{ top: 6, right: 4, bottom: 0, left: -8 }}>
          <XAxis
            dataKey="year"
            tick={{ fontSize: 10, fill: "#5a6472" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "#5a6472" }}
            axisLine={false}
            tickLine={false}
            width={44}
            domain={[Math.floor(lo - pad), Math.ceil(hi + pad)]}
            tickFormatter={(v: number) => `${Math.round(v / 1000)}k`}
          />
          <Tooltip
            contentStyle={{
              fontSize: 11,
              borderRadius: 8,
              border: "1px solid #e3e7ed",
            }}
            labelFormatter={(y) => `${y}`}
            formatter={(value, name, item) => {
              const r = item.payload as (typeof rows)[number];
              if (name === "median")
                return [`${num(r.median)} €/m² · ${num(r.n)} sales`, "median"];
              if (name === "range")
                return [`${num(r.p25)} – ${num(r.p75)} €/m²`, "middle half"];
              return [String(value), String(name)];
            }}
          />
          <Area
            dataKey="range"
            stroke="none"
            fill="#2f6df6"
            fillOpacity={0.13}
            isAnimationActive={false}
            activeDot={false}
          />
          <Line
            dataKey="median"
            stroke="#2f6df6"
            strokeWidth={2}
            dot={{ r: 2.5 }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
